import { logRocketError } from "@/utils/logUtils";
import { SingleBitPerPixelBitmap } from "./pdf417";
import { write1s } from "./utils";

// the code 128 barcode spec: https://en.wikipedia.org/wiki/Code_128
//
// LOOKUP TABLE
// values are a string, but each digit is ascii 1->4 inclusive, and represents
// the length of the bar for this codepoint (each symbol is 6 bars, ranging from 1->4 units in width)
//
// code 128 sort-of has three independent lookup tables, CodeA, CodeB, and CodeC.
//
// special commands inside each table let you switch between which lookup table you're using.
//
// the initial lookup table is decided by the 'start symbol'.
//
// we only use CodeB, because it's sufficient, so that's all that's here.
const myLookupTableCodeB: {[key: number]: string} = {
  [0x20]: "212222", // " "
  [0x21]: "222122", // "!"
  [0x22]: "222221", // "\""
  [0x23]: "121223", // "#"
  [0x24]: "121322", // "$"
  [0x25]: "131222", // "%"
  [0x26]: "122213", // "&"
  [0x27]: "122312", // "'"
  [0x28]: "132212", // "("
  [0x29]: "221213", // ")"
  [0x2A]: "221312", // "*"
  [0x2B]: "231212", // "+"
  [0x2C]: "112232", // ","
  [0x2D]: "122132", // "-"
  [0x2E]: "122231", // "."
  [0x2F]: "113222", // "/"
  [0x30]: "123122", // "0"
  [0x31]: "123221", // "1"
  [0x32]: "223211", // "2"
  [0x33]: "221132", // "3"
  [0x34]: "221231", // "4"
  [0x35]: "213212", // "5"
  [0x36]: "223112", // "6"
  [0x37]: "312131", // "7"
  [0x38]: "311222", // "8"
  [0x39]: "321122", // "9"
  [0x3A]: "321221", // ":"
  [0x3B]: "312212", // ";"
  [0x3C]: "322112", // "<"
  [0x3D]: "322211", // "="
  [0x3E]: "212123", // ">"
  [0x3F]: "212321", // "?"
  [0x40]: "232121", // "@"
  [0x41]: "111323", // "A"
  [0x42]: "131123", // "B"
  [0x43]: "131321", // "C"
  [0x44]: "112313", // "D"
  [0x45]: "132113", // "E"
  [0x46]: "132311", // "F"
  [0x47]: "211313", // "G"
  [0x48]: "231113", // "H"
  [0x49]: "231311", // "I"
  [0x4A]: "112133", // "J"
  [0x4B]: "112331", // "K"
  [0x4C]: "132131", // "L"
  [0x4D]: "113123", // "M"
  [0x4E]: "113321", // "N"
  [0x4F]: "133121", // "O"
  [0x50]: "313121", // "P"
  [0x51]: "211331", // "Q"
  [0x52]: "231131", // "R"
  [0x53]: "213113", // "S"
  [0x54]: "213311", // "T"
  [0x55]: "213131", // "U"
  [0x56]: "311123", // "V"
  [0x57]: "311321", // "W"
  [0x58]: "331121", // "X" 
  [0x59]: "312113", // "Y"
  [0x5A]: "312311", // "Z"
  [0x5B]: "332111", // "["
  [0x5C]: "314111", // "]"
  [0x5D]: "221411", // "\\"
  [0x5E]: "431111", // "^"
  [0x5F]: "111224", // "_"
  [0x60]: "111422", // "`"
  [0x61]: "121124", // "a"
  [0x62]: "121421", // "b"
  [0x63]: "141122", // "c"
  [0x64]: "141221", // "d"
  [0x65]: "112214", // "e"
  [0x66]: "112412", // "f"
  [0x67]: "122114", // "g"
  [0x68]: "122411", // "h"
  [0x69]: "142112", // "i"
  [0x6A]: "142211", // "j"
  [0x6B]: "241211", // "k"
  [0x6C]: "221114", // "l"
  [0x6D]: "413111", // "m"
  [0x6E]: "241112", // "n"
  [0x6F]: "134111", // "o"
  [0x70]: "111242", // "p"
  [0x71]: "121142", // "q"
  [0x72]: "121241", // "r"
  [0x73]: "114212", // "s"
  [0x74]: "124112", // "t"
  [0x75]: "124211", // "u"
  [0x76]: "411212", // "v"
  [0x77]: "421112", // "w"
  [0x78]: "421211", // "x"
  [0x79]: "212141", // "y"
  [0x7A]: "214121", // "z"
  [0x7B]: "412121", // "{"
  [0x7C]: "111143", // "|"
  [0x7D]: "111341", // "}"
  [0x7E]: "131141", // "~"
  [0x7F]: "114113", // "DEL"
  [0x80]: "114311", // "FNC 3"
  [0x81]: "411113", // "FNC 2"
  [0x82]: "411311", // "Shift A"
  [0x83]: "113141", // "Code C"
  [0x84]: "114131", // "FNC 4"
  [0x85]: "311141", // "Code A"
  [0x86]: "411131", // "FNC 1"
};

let lastErr;
const err = (message: string): SingleBitPerPixelBitmap => {
  if (message === lastErr) return { width: 0, height: 0, data: null };
  lastErr = message;
  
  logRocketError(`Error generating code128 barcode: ${message}`);

  return { width: 0, height: 0, data: null };
}

// run some tests on init, can remove in prod... just trying to be safe.
// all values should add to 11, and no value should be present more than once.
// we cover the entire printable ASCII range, including spaces, which is probably
// more than enough - amazon doesn't say what their FNSKUs can and can't be, but
// in practice they are nearly always only numbers 0-9 and uppercase ASCII. we're
// probably overcompensating.
const test = () => {
  const start = performance.now();
  if (Object.keys(myLookupTableCodeB).length !== 103) {
    return err(`lookup table does not have 103 entries exactly (instead has ${Object.keys(myLookupTableCodeB).length})! We need that many, because the checksum character is determined by a 'checksum % 103' operation, which would be out-of-bounds of a table of size < 103.`);
  }

  const set = new Set<string>();
  for (const [key, value] of Object.entries(myLookupTableCodeB)) {
    if (set.has(value)) {
      return err(`key: ${key} has value ${value} which is shared with at least one other key...`);
    } else {
      set.add(value);
    }
    if (value.split("").reduce((acc, curr) => acc + Number(curr), 0) !== 11) {
      return err(`key: ${key} has value: ${value} which doesn't add to 11.`);
    }
    if (value.split("").filter((_, i) => i % 2 === 0).reduce((acc, curr) => acc + Number(curr), 0) % 2 !== 0) {
      return err(`key: ${key} has value: ${value} which has bars (characters 0, 2, 4) that don't add to an even number.`);
    }
    if (value.split("").filter((_, i) => i % 2 !== 0).reduce((acc, curr) => acc + Number(curr), 0) % 2 === 0) {
      return err(`key: ${key} has value: ${value} which has spaces (characters 1, 3, 5) that don't add to an odd number.`);  
    }
  }
  // console.info(`code128 tests took: ${performance.now() - start}ms`);
}
//test();


export function code128B(data: string): SingleBitPerPixelBitmap {
  let bitsSoFar = 0;
  // 10*2 for each quiet zone, 11 for start symbol, 11 for check symbol, 13 for stop symbol, 11 per char in fnsku
  // round up to nearest byte. we pad the image data with whitespace if necessary at the end.
  const totalBytes = Math.ceil((10*2 + 11 + 11 + 13 + 11*data.length)/8);
  const a = new Uint8Array(totalBytes);
  // uint8array is 0-initialized by default. this means we only have to write 1's, or 'white'

  // write the starting dead zone...
  bitsSoFar = write1s(a, bitsSoFar, 10);

  // write the start symbol... 
  // this is probably confusing. the start symbol for CodeB is given by the 'widths': 211214,
  // which means, a black bar of width 2, a space of width 1, a black bar of width 1, etc.
  // that is what we are writing here. 
  // recall that what we really are writing is just the white-spaces, because the buffer is entirely
  // 0's or black to begin. all symbols work like this.
  // we also need to compute a checksum, the formula for which is given here: https://en.wikipedia.org/wiki/Code_128#Check_digit_calculation
  let checkSum = 104;
  bitsSoFar += 2;
  bitsSoFar = write1s(a, bitsSoFar, 1);
  bitsSoFar += 1;
  bitsSoFar = write1s(a, bitsSoFar, 2)
  bitsSoFar += 1;
  bitsSoFar = write1s(a, bitsSoFar, 4);

  // write the data... (and compute checksum)
  for (let i = 0; i < data.length; i++) {
    const code = data.charCodeAt(i);
    const widths = myLookupTableCodeB[code];
    if (!widths) return err(`data: ${data} has character ${data[i]} with codepoint: ${code} which we don't encode in our table.`);
    checkSum += (code - 0x20) * (i + 1);
    for (let j = 0; j < 6; j++) {
      const width = +widths[j];
      if (j % 2 === 0) {
        bitsSoFar += width;
      } else {
        bitsSoFar = write1s(a, bitsSoFar, width);
      }
    }
  }

  // write the check symbol...
  const remainderModulo103 = checkSum % 103;
  const widths = myLookupTableCodeB[remainderModulo103+0x20];
  if (!widths) return err(`got checksum remainder modulo 103 of ${remainderModulo103}, which isn't a key in our table!`);
  for (let j = 0; j < 6; j++) {
    const width = +widths[j];
    if (j % 2 === 0) {
      bitsSoFar += width;
    } else {
      bitsSoFar = write1s(a, bitsSoFar, width);
    }
  }

  // write the stop symbol...
  bitsSoFar += 2;
  bitsSoFar = write1s(a, bitsSoFar, 3);
  bitsSoFar += 3;
  bitsSoFar = write1s(a, bitsSoFar, 1)
  bitsSoFar += 1;
  bitsSoFar = write1s(a, bitsSoFar, 1);
  bitsSoFar += 2; // stop symbol has an extra 2 black bars at the end - only symbol with 7 'things'.

  // and the terminating dead zone...
  bitsSoFar = write1s(a, bitsSoFar, 10);

  // any leftover bits should be set to 1s.
  // we discard the return value here, so logging 'bitsSoFar' will show you the number of bits not including these padding bits.
  write1s(a, bitsSoFar, totalBytes*8-bitsSoFar);

  return { width: totalBytes*8, height: 1, data: a };
}
